import React, { useState } from "react";
import StepOnePersonal from "./StepOnePersonal";
import StepTwoVerification from "./StepTwoVerification";
import StepThreeLivePhoto from "./StepThreeLivePhoto";
import "./registerAsDoctor.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Camera,
  Check,
  FileCheck2,
  Loader2,
  ShieldCheck,
  UserCheck,
} from "lucide-react";

const flowSteps = [
  {
    id: 1,
    label: "Profile",
    short: "Identity and contact",
    icon: UserCheck,
    asideTitle: "A few details away from joining Zaviramed.",
    asideText:
      "Start with the information we need to create your doctor profile and contact you about patient care.",
    infoTitle: "What happens here",
    infoText:
      "Use your real name, active phone number, and email address. Specialist details are optional and can be reviewed later.",
    bullets: [
      "Full name and contact details",
      "Home address for verification",
      "Optional specialist profile",
    ],
    formTitle: "Create your doctor profile",
    formText:
      "Tell us who you are so the admin team can review your account correctly.",
  },
  {
    id: 2,
    label: "Credentials",
    short: "MDCN, NIN, licence",
    icon: FileCheck2,
    asideTitle: "Upload the documents that prove you can practise.",
    asideText:
      "Zaviramed only admits verified medical professionals, so this step protects patients and the doctor network.",
    infoTitle: "Keep the document clear",
    infoText:
      "Upload a readable licence photo or scan. Blurry documents can slow down approval.",
    bullets: ["MDCN number", "NIN number", "Clear medical licence image"],
    formTitle: "Verify your credentials",
    formText:
      "Add your MDCN and NIN, then upload your medical licence for admin review.",
  },
  {
    id: 3,
    label: "Live photo",
    short: "Final identity check",
    icon: Camera,
    asideTitle: "Take a quick live photo to finish your setup.",
    asideText:
      "This confirms that the doctor creating the account matches the submitted credentials.",
    infoTitle: "Photo guidance",
    infoText:
      "Use a bright space, face the camera directly, and take the photo without filters.",
    bullets: ["Good lighting", "Face clearly visible", "Used only for verification"],
    formTitle: "Take your verification photo",
    formText:
      "Capture a clear live photo so the admin team can complete your doctor approval.",
  },
];

const RegisterAsDoctor = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [liscense_photo, setLiscencePhoto] = useState(null);
  const [doctor_photo, setDoctorPhoto] = useState(null);

  const nextStep = () => setStep((s) => Math.min(s + 1, flowSteps.length));
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const activeStep = flowSteps.find((item) => item.id === step) || flowSteps[0];
  const progressPercent = Math.round((step / flowSteps.length) * 100);

  const uploadIMG = async (file) => {
    const uploadFormData = new FormData();
    uploadFormData.append("document", file);

    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/upload",
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data.url;
    } catch (error) {
      console.error("Request failed:", error);

      let message = "An unexpected error occurred";

      if (error?.response) {
        message =
          error.response.data?.message ||
          error.response.data?.error ||
          "Request failed: the image is too large. Please reduce it to approximately 1MB or less.";
      } else if (error?.request) {
        message = "No response from server. Check your connection.";
      } else if (error?.message) {
        message = error.message;
      }

      alert(message);
    }
  };

  const completeRegistration = async (personalPhotoUrl) => {
    try {
      setLoading(true);
      setProgress(10);

      const payload = {
        ...formData,
        liscensePhoto: liscense_photo,
        personalPhoto: personalPhotoUrl || doctor_photo || formData.livePhotoUrl,
      };

      setProgress(30);

      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/doctors",
        payload
      );

      setProgress(70);

      localStorage.setItem("doctor", JSON.stringify(res.data.doctor));
      localStorage.setItem("token", res.data.token);

      setProgress(99);

      setTimeout(() => {
        navigate("/dashboard/home");
      }, 500);
    } catch (error) {
      console.error("Request failed:", error);

      let message = "An unexpected error occurred";

      if (error?.response) {
        message =
          error.response.data?.message ||
          error.response.data?.error ||
          `Request failed (${error.response.status})`;
      } else if (error?.request) {
        message = "No response from server. Check your connection.";
      } else if (error?.message) {
        message = error.message;
      }

      alert(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 2000);
    }
  };

  return (
    <main className="doctor-signup-screen">
      <aside className="doctor-signup-aside" aria-label="Registration progress">
        <div className="doctor-signup-aside-pattern" />

        <div className="doctor-signup-aside-top">
          <button
            type="button"
            className="doctor-signup-brand "
            onClick={() => navigate("/")}
            aria-label="Go to Zaviramed home"
          >
            <span className="doctor-signup-logo">
              <img src="/zavira_nobg.png" alt="Zaviramed" />
            </span>
            <span>
              <strong>
                Zavira<span>Med</span>
              </strong>
              <small>Doctor onboarding</small>
            </span>
          </button>

          {/* <div className="doctor-signup-flow-label">
            <ShieldCheck size={15} />
            Secure registration
          </div> */}

          {/* <h1>{activeStep.asideTitle}</h1> */}
          <p className="hidden lg:block">{activeStep.asideText}</p>

          <div className="doctor-signup-progress-panel mt-5">
            <div className="doctor-signup-progress-head">
              <span>Step {step} of {flowSteps.length}</span>
              <strong>{activeStep.label}</strong>
            </div>

            <div className="doctor-signup-progress-bar" aria-hidden="true">
              <span style={{ width: `${progressPercent}%` }} />
            </div>

            <ol className="doctor-signup-step-list">
              {flowSteps.map((item) => {
                const Icon = item.icon;
                const isDone = step > item.id;
                const isActive = step === item.id;

                return (
                  <li
                    key={item.id}
                    className={[
                      "doctor-signup-step-item",
                      isActive ? "is-active" : "",
                      isDone ? "is-done" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <span className="doctor-signup-step-icon">
                      {isDone ? <Check size={16} /> : <Icon size={16} />}
                    </span>
                    <span>
                      <strong>{item.label}</strong>
                      <small>{item.short}</small>
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

      
      </aside>

      <section className="doctor-signup-content" aria-label="Doctor signup form">
        <div className="doctor-signup-form-wrap" style={{marginTop:'-40px'}}>
          <div className="doctor-signup-form-header">
            <h2>{activeStep.formTitle}</h2>
            <p>{activeStep.formText}</p>
          </div>

          <div className="doctor-signup-step-content">
            {step === 1 && (
              <StepOnePersonal
                formData={formData}
                setFormData={setFormData}
                nextStep={nextStep}
              />
            )}

            {step === 2 && (
              <StepTwoVerification
                uploadIMG={uploadIMG}
                formData={formData}
                setFormData={setFormData}
                nextStep={nextStep}
                prevStep={prevStep}
                setLiscencePhoto={setLiscencePhoto}
              />
            )}

            {step === 3 && (
              <StepThreeLivePhoto
                formData={formData}
                setFormData={setFormData}
                prevStep={prevStep}
                completeRegistration={completeRegistration}
                uploadIMG={uploadIMG}
                setDoctorPhoto={setDoctorPhoto}
              />
            )}
          </div>

          <div className="doctor-signup-support-row">
            <span>
              <ShieldCheck size={15} />
              Secure verification for licensed doctors.
            </span>
            <button type="button" onClick={() => navigate("/login")}>
              Already registered? Log in
            </button>
          </div>
        </div>
      </section>

      {loading && <LoaderOverlay progress={progress} />}
    </main>
  );
};

export default RegisterAsDoctor;

const LoaderOverlay = ({ progress }) => {
  return (
    <div className="doctor-signup-loader">
      <div className="doctor-signup-loader-card">
        <div className="doctor-signup-loader-icon">
          <Loader2 size={28} className="animate-spin" />
        </div>

        <h3>Completing registration</h3>
        <p>
          Please wait while we verify your details and prepare your doctor
          dashboard.
        </p>

        <div className="doctor-signup-loader-bar">
          <span style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }} />
        </div>

        <div className="doctor-signup-loader-meta">
          <span>Progress</span>
          <strong>{progress}%</strong>
        </div>
      </div>
    </div>
  );
};
